/**
 * Skins & Customization System for Wooorms
 *
 * Kid-friendly skin collections organized by theme.
 * Fun, colorful skins that everyone will love!
 */

/**
 * Skin type enum - what kind of skin is being used
 */
export enum SkinType {
  COLOR = 'color',
  PATTERN = 'pattern',
  FLAG = 'flag',
}

/**
 * Pattern types for pattern skins
 */
export enum PatternType {
  // Core patterns
  SOLID = 'solid',
  STRIPED = 'striped',
  SPOTTED = 'spotted',
  GRADIENT = 'gradient',
  RAINBOW = 'rainbow',
  ZIGZAG = 'zigzag',
  CHECKER = 'checker',

  // Animal patterns
  TIGER = 'tiger',
  ZEBRA = 'zebra',
  CAMO = 'camo',

  // Elemental patterns
  LAVA = 'lava',
  FROST = 'frost',
  ELECTRIC = 'electric',
  TOXIC = 'toxic',

  // Special patterns
  NEON = 'neon',
  METALLIC = 'metallic',
  GALAXY = 'galaxy',
  SUNSET = 'sunset',

  // Animated patterns
  PLASMA = 'plasma',
  HOLOGRAPHIC = 'holographic',
  VOID = 'void',
  CYBER = 'cyber',
  CRYSTAL = 'crystal',
  GLITCH = 'glitch',

  // Dynamic patterns
  FIRE = 'fire',
  OCEAN = 'ocean',
  AURORA = 'aurora',
  DISCO = 'disco',
  MATRIX = 'matrix',
  PULSE = 'pulse',
  SCALES = 'scales',
  LIGHTNING = 'lightning',
  CHROMATIC = 'chromatic',
  EMBER = 'ember',
  HYPNOTIC = 'hypnotic',
  PIXELATE = 'pixelate',

  // Nature patterns
  BEEHIVE = 'beehive',
  MARBLE = 'marble',
  WOOD = 'wood',
  CORAL = 'coral',
  FEATHER = 'feather',

  // Tech patterns
  CIRCUIT = 'circuit',
  HOLOGRAM = 'hologram',
  QUANTUM = 'quantum',
  BINARY = 'binary',

  // Artistic patterns
  MOSAIC = 'mosaic',
  WATERCOLOR = 'watercolor',
  TRIBAL = 'tribal',
  ORIGAMI = 'origami',
}

/**
 * Skin collection enum for organizing skins
 */
export enum SkinCollection {
  ANIMALS = 'animals',
  BUGS = 'bugs',
  OCEAN = 'ocean',
  DINOSAURS = 'dinosaurs',
  CANDY = 'candy',
  FRUITS = 'fruits',
  FIRE = 'fire',
  ICE = 'ice',
  SPACE = 'space',
  RAINBOW = 'rainbow',
  FANTASY = 'fantasy',
  SUPERHEROES = 'superheroes',
  SPORTS = 'sports',
  HOLIDAYS = 'holidays',
}

/**
 * Collection display info - icons, names, descriptions for UI
 */
export interface CollectionInfo {
  id: SkinCollection;
  name: string;
  icon: string;
  description: string;
  order: number;
}

/**
 * Collection metadata for UI display
 */
export const COLLECTION_INFO: Record<SkinCollection, CollectionInfo> = {
  [SkinCollection.ANIMALS]: {
    id: SkinCollection.ANIMALS,
    name: 'Animals',
    icon: '🐾',
    description: 'Cute animals everyone loves!',
    order: 1,
  },
  [SkinCollection.BUGS]: {
    id: SkinCollection.BUGS,
    name: 'Bugs & Critters',
    icon: '🐛',
    description: 'Cute crawlies!',
    order: 2,
  },
  [SkinCollection.OCEAN]: {
    id: SkinCollection.OCEAN,
    name: 'Ocean',
    icon: '🐠',
    description: 'Under the sea!',
    order: 3,
  },
  [SkinCollection.DINOSAURS]: {
    id: SkinCollection.DINOSAURS,
    name: 'Dinosaurs',
    icon: '🦖',
    description: 'Roar!',
    order: 4,
  },
  [SkinCollection.CANDY]: {
    id: SkinCollection.CANDY,
    name: 'Candy & Sweets',
    icon: '🍭',
    description: 'Yummy!',
    order: 5,
  },
  [SkinCollection.FRUITS]: {
    id: SkinCollection.FRUITS,
    name: 'Fruits',
    icon: '🍓',
    description: 'Fresh and colorful!',
    order: 6,
  },
  [SkinCollection.FIRE]: {
    id: SkinCollection.FIRE,
    name: 'Fire & Hot',
    icon: '🔥',
    description: 'Blazing cool!',
    order: 7,
  },
  [SkinCollection.ICE]: {
    id: SkinCollection.ICE,
    name: 'Ice & Cold',
    icon: '❄️',
    description: 'Frosty cool!',
    order: 8,
  },
  [SkinCollection.SPACE]: {
    id: SkinCollection.SPACE,
    name: 'Space',
    icon: '🚀',
    description: 'To infinity!',
    order: 9,
  },
  [SkinCollection.RAINBOW]: {
    id: SkinCollection.RAINBOW,
    name: 'Rainbow & Colors',
    icon: '🌈',
    description: 'Bright and happy!',
    order: 10,
  },
  [SkinCollection.FANTASY]: {
    id: SkinCollection.FANTASY,
    name: 'Fantasy & Magic',
    icon: '🦄',
    description: 'Magical!',
    order: 11,
  },
  [SkinCollection.SUPERHEROES]: {
    id: SkinCollection.SUPERHEROES,
    name: 'Superheroes',
    icon: '⚡',
    description: 'Pow! Zap!',
    order: 12,
  },
  [SkinCollection.SPORTS]: {
    id: SkinCollection.SPORTS,
    name: 'Sports',
    icon: '⚽',
    description: 'Game on!',
    order: 13,
  },
  [SkinCollection.HOLIDAYS]: {
    id: SkinCollection.HOLIDAYS,
    name: 'Holidays',
    icon: '🎄',
    description: 'Festive fun!',
    order: 14,
  },
};

/**
 * Get all collections in display order
 */
export function getCollectionsInOrder(): CollectionInfo[] {
  return Object.values(COLLECTION_INFO).sort((a, b) => a.order - b.order);
}

/**
 * Color skin definition
 */
export interface ColorSkin {
  type: SkinType.COLOR;
  id: string;
  name: string;
  primaryColor: string;
}

/**
 * Pattern skin definition
 */
export interface PatternSkin {
  type: SkinType.PATTERN;
  id: string;
  name: string;
  pattern: PatternType;
  primaryColor: string;
  secondaryColor?: string;
  collection?: SkinCollection;
}

/**
 * Flag skin definition
 */
export interface FlagSkin {
  type: SkinType.FLAG;
  id: string;
  name: string;
  country: string;
  colors: string[];
}

/**
 * Union type for all skin types
 */
export type PlayerSkin = ColorSkin | PatternSkin | FlagSkin;

/**
 * Serializable skin data for network transmission
 */
export interface SkinData {
  type: SkinType;
  id: string;
  primaryColor: string;
  secondaryColor?: string;
  pattern?: PatternType;
  colors?: string[];
}

// ═══════════════════════════════════════════════════════════════════════════════
//  COLOR SKINS - Simple, bright, joyful colors
// ═══════════════════════════════════════════════════════════════════════════════

export const COLOR_SKINS: ColorSkin[] = [
  // Basic bright colors
  { type: SkinType.COLOR, id: 'red', name: 'Red', primaryColor: '#ff4757' },
  { type: SkinType.COLOR, id: 'orange', name: 'Orange', primaryColor: '#ff6b35' },
  { type: SkinType.COLOR, id: 'yellow', name: 'Yellow', primaryColor: '#ffd32a' },
  { type: SkinType.COLOR, id: 'green', name: 'Green', primaryColor: '#2ed573' },
  { type: SkinType.COLOR, id: 'blue', name: 'Blue', primaryColor: '#1e90ff' },
  { type: SkinType.COLOR, id: 'purple', name: 'Purple', primaryColor: '#a55eea' },
  { type: SkinType.COLOR, id: 'pink', name: 'Pink', primaryColor: '#ff6b9d' },
  { type: SkinType.COLOR, id: 'cyan', name: 'Cyan', primaryColor: '#00d2d3' },

  // Fun variations
  { type: SkinType.COLOR, id: 'lime', name: 'Lime', primaryColor: '#7bed9f' },
  { type: SkinType.COLOR, id: 'coral', name: 'Coral', primaryColor: '#ff7f50' },
  { type: SkinType.COLOR, id: 'teal', name: 'Teal', primaryColor: '#4ecdc4' },
  { type: SkinType.COLOR, id: 'magenta', name: 'Magenta', primaryColor: '#ff00ff' },
  { type: SkinType.COLOR, id: 'gold', name: 'Gold', primaryColor: '#ffc107' },
  { type: SkinType.COLOR, id: 'aqua', name: 'Aqua', primaryColor: '#7fdbff' },
  { type: SkinType.COLOR, id: 'violet', name: 'Violet', primaryColor: '#9b59b6' },
  { type: SkinType.COLOR, id: 'mint', name: 'Mint', primaryColor: '#00fa9a' },

  // Pastels
  { type: SkinType.COLOR, id: 'peach', name: 'Peach', primaryColor: '#ffb8b8' },
  { type: SkinType.COLOR, id: 'lavender', name: 'Lavender', primaryColor: '#c8a2ff' },
  { type: SkinType.COLOR, id: 'sky', name: 'Sky', primaryColor: '#87ceeb' },
  { type: SkinType.COLOR, id: 'cream', name: 'Cream', primaryColor: '#fffacd' },

  // Bold & fun
  { type: SkinType.COLOR, id: 'hotpink', name: 'Hot Pink', primaryColor: '#ff1493' },
  { type: SkinType.COLOR, id: 'electric', name: 'Electric', primaryColor: '#00ff00' },
  { type: SkinType.COLOR, id: 'sunset', name: 'Sunset', primaryColor: '#ff6348' },
  { type: SkinType.COLOR, id: 'ocean', name: 'Ocean', primaryColor: '#3498db' },
  { type: SkinType.COLOR, id: 'berry', name: 'Berry', primaryColor: '#e84393' },
  { type: SkinType.COLOR, id: 'lemon', name: 'Lemon', primaryColor: '#f9ed69' },
  { type: SkinType.COLOR, id: 'grape', name: 'Grape', primaryColor: '#6c5ce7' },
  { type: SkinType.COLOR, id: 'tangerine', name: 'Tangerine', primaryColor: '#f39c12' },

  // Classic
  { type: SkinType.COLOR, id: 'white', name: 'White', primaryColor: '#ffffff' },
  { type: SkinType.COLOR, id: 'black', name: 'Black', primaryColor: '#2d3436' },
];

// ═══════════════════════════════════════════════════════════════════════════════
//  PATTERN SKINS - Organized by kid-friendly collections
// ═══════════════════════════════════════════════════════════════════════════════

export const PATTERN_SKINS: PatternSkin[] = [
  // ═══════════════════════════════════════════════════════════════════════════════
  //  🐾 ANIMALS - The classics every kid loves!
  // ═══════════════════════════════════════════════════════════════════════════════

  // Cats & Dogs
  { type: SkinType.PATTERN, id: 'animal-kitty', name: 'Kitty Cat', pattern: PatternType.TIGER, primaryColor: '#ff8c00', secondaryColor: '#fff5e6', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-tabby', name: 'Tabby', pattern: PatternType.TIGER, primaryColor: '#a0522d', secondaryColor: '#ffdab9', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-puppy', name: 'Puppy', pattern: PatternType.SPOTTED, primaryColor: '#f5f5dc', secondaryColor: '#8b4513', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-dalmatian', name: 'Dalmatian', pattern: PatternType.SPOTTED, primaryColor: '#ffffff', secondaryColor: '#000000', collection: SkinCollection.ANIMALS },

  // Farm Animals
  { type: SkinType.PATTERN, id: 'animal-cow', name: 'Cow', pattern: PatternType.SPOTTED, primaryColor: '#ffffff', secondaryColor: '#1a1a1a', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-piggy', name: 'Piggy', pattern: PatternType.GRADIENT, primaryColor: '#ffb6c1', secondaryColor: '#ff69b4', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-duckling', name: 'Duckling', pattern: PatternType.GRADIENT, primaryColor: '#ffd700', secondaryColor: '#ff8c00', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-chick', name: 'Chick', pattern: PatternType.GRADIENT, primaryColor: '#ffff00', secondaryColor: '#ffa500', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-sheep', name: 'Sheep', pattern: PatternType.GRADIENT, primaryColor: '#f5f5f5', secondaryColor: '#d3d3d3', collection: SkinCollection.ANIMALS },

  // Wild Animals
  { type: SkinType.PATTERN, id: 'animal-bunny', name: 'Bunny', pattern: PatternType.GRADIENT, primaryColor: '#ffffff', secondaryColor: '#ffb6c1', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-panda', name: 'Panda', pattern: PatternType.CHECKER, primaryColor: '#ffffff', secondaryColor: '#000000', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-teddy', name: 'Teddy Bear', pattern: PatternType.GRADIENT, primaryColor: '#8b4513', secondaryColor: '#deb887', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-lion', name: 'Lion', pattern: PatternType.GRADIENT, primaryColor: '#daa520', secondaryColor: '#8b4513', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-tiger', name: 'Tiger', pattern: PatternType.TIGER, primaryColor: '#ff6600', secondaryColor: '#000000', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-zebra', name: 'Zebra', pattern: PatternType.ZEBRA, primaryColor: '#ffffff', secondaryColor: '#000000', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-giraffe', name: 'Giraffe', pattern: PatternType.SPOTTED, primaryColor: '#f4a460', secondaryColor: '#8b4513', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-elephant', name: 'Elephant', pattern: PatternType.GRADIENT, primaryColor: '#808080', secondaryColor: '#a9a9a9', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-monkey', name: 'Monkey', pattern: PatternType.GRADIENT, primaryColor: '#8b4513', secondaryColor: '#deb887', collection: SkinCollection.ANIMALS },

  // Cute Critters
  { type: SkinType.PATTERN, id: 'animal-fox', name: 'Fox', pattern: PatternType.GRADIENT, primaryColor: '#ff6347', secondaryColor: '#ffffff', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-wolf', name: 'Wolf', pattern: PatternType.GRADIENT, primaryColor: '#696969', secondaryColor: '#c0c0c0', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-deer', name: 'Deer', pattern: PatternType.SPOTTED, primaryColor: '#d2691e', secondaryColor: '#ffffff', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-koala', name: 'Koala', pattern: PatternType.GRADIENT, primaryColor: '#808080', secondaryColor: '#d3d3d3', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-penguin', name: 'Penguin', pattern: PatternType.GRADIENT, primaryColor: '#000000', secondaryColor: '#ffffff', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-hamster', name: 'Hamster', pattern: PatternType.GRADIENT, primaryColor: '#deb887', secondaryColor: '#ffffff', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-mouse', name: 'Mouse', pattern: PatternType.GRADIENT, primaryColor: '#c0c0c0', secondaryColor: '#ffb6c1', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-raccoon', name: 'Raccoon', pattern: PatternType.STRIPED, primaryColor: '#696969', secondaryColor: '#000000', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-skunk', name: 'Skunk', pattern: PatternType.STRIPED, primaryColor: '#000000', secondaryColor: '#ffffff', collection: SkinCollection.ANIMALS },
  { type: SkinType.PATTERN, id: 'animal-sloth', name: 'Sloth', pattern: PatternType.GRADIENT, primaryColor: '#8b7355', secondaryColor: '#a0522d', collection: SkinCollection.ANIMALS },

  // ═══════════════════════════════════════════════════════════════════════════════
  //  🐛 BUGS & CRITTERS - Cute crawlies kids love!
  // ═══════════════════════════════════════════════════════════════════════════════

  { type: SkinType.PATTERN, id: 'bug-ladybug', name: 'Ladybug', pattern: PatternType.SPOTTED, primaryColor: '#e60000', secondaryColor: '#000000', collection: SkinCollection.BUGS },
  { type: SkinType.PATTERN, id: 'bug-bumblebee', name: 'Bumblebee', pattern: PatternType.STRIPED, primaryColor: '#ffd700', secondaryColor: '#000000', collection: SkinCollection.BUGS },
  { type: SkinType.PATTERN, id: 'bug-butterfly', name: 'Butterfly', pattern: PatternType.RAINBOW, primaryColor: '#ff69b4', secondaryColor: '#9400d3', collection: SkinCollection.BUGS },
  { type: SkinType.PATTERN, id: 'bug-caterpillar', name: 'Caterpillar', pattern: PatternType.STRIPED, primaryColor: '#32cd32', secondaryColor: '#90ee90', collection: SkinCollection.BUGS },
  { type: SkinType.PATTERN, id: 'bug-firefly', name: 'Firefly', pattern: PatternType.NEON, primaryColor: '#ffff00', secondaryColor: '#2f2f2f', collection: SkinCollection.BUGS },
  { type: SkinType.PATTERN, id: 'bug-dragonfly', name: 'Dragonfly', pattern: PatternType.HOLOGRAPHIC, primaryColor: '#00ced1', secondaryColor: '#4169e1', collection: SkinCollection.BUGS },
  { type: SkinType.PATTERN, id: 'bug-grasshopper', name: 'Grasshopper', pattern: PatternType.GRADIENT, primaryColor: '#228b22', secondaryColor: '#90ee90', collection: SkinCollection.BUGS },
  { type: SkinType.PATTERN, id: 'bug-ant', name: 'Ant', pattern: PatternType.GRADIENT, primaryColor: '#1a1a1a', secondaryColor: '#8b0000', collection: SkinCollection.BUGS },
  { type: SkinType.PATTERN, id: 'bug-snail', name: 'Snail', pattern: PatternType.GRADIENT, primaryColor: '#deb887', secondaryColor: '#8b7355', collection: SkinCollection.BUGS },
  { type: SkinType.PATTERN, id: 'bug-worm', name: 'Worm', pattern: PatternType.GRADIENT, primaryColor: '#ffb6c1', secondaryColor: '#cd853f', collection: SkinCollection.BUGS },
  { type: SkinType.PATTERN, id: 'bug-beetle', name: 'Beetle', pattern: PatternType.METALLIC, primaryColor: '#228b22', secondaryColor: '#006400', collection: SkinCollection.BUGS },
  { type: SkinType.PATTERN, id: 'bug-spider', name: 'Spider', pattern: PatternType.SPOTTED, primaryColor: '#1a1a1a', secondaryColor: '#8b0000', collection: SkinCollection.BUGS },

  // ═══════════════════════════════════════════════════════════════════════════════
  //  🐠 OCEAN - Under the sea adventures!
  // ═══════════════════════════════════════════════════════════════════════════════

  { type: SkinType.PATTERN, id: 'ocean-clownfish', name: 'Clownfish', pattern: PatternType.STRIPED, primaryColor: '#ff6600', secondaryColor: '#ffffff', collection: SkinCollection.OCEAN },
  { type: SkinType.PATTERN, id: 'ocean-shark', name: 'Shark', pattern: PatternType.GRADIENT, primaryColor: '#708090', secondaryColor: '#f5f5f5', collection: SkinCollection.OCEAN },
  { type: SkinType.PATTERN, id: 'ocean-dolphin', name: 'Dolphin', pattern: PatternType.GRADIENT, primaryColor: '#4682b4', secondaryColor: '#b0c4de', collection: SkinCollection.OCEAN },
  { type: SkinType.PATTERN, id: 'ocean-whale', name: 'Whale', pattern: PatternType.GRADIENT, primaryColor: '#4169e1', secondaryColor: '#87ceeb', collection: SkinCollection.OCEAN },
  { type: SkinType.PATTERN, id: 'ocean-octopus', name: 'Octopus', pattern: PatternType.SPOTTED, primaryColor: '#9932cc', secondaryColor: '#ff69b4', collection: SkinCollection.OCEAN },
  { type: SkinType.PATTERN, id: 'ocean-jellyfish', name: 'Jellyfish', pattern: PatternType.HOLOGRAPHIC, primaryColor: '#ff69b4', secondaryColor: '#00ffff', collection: SkinCollection.OCEAN },
  { type: SkinType.PATTERN, id: 'ocean-seahorse', name: 'Seahorse', pattern: PatternType.GRADIENT, primaryColor: '#ffd700', secondaryColor: '#ff6347', collection: SkinCollection.OCEAN },
  { type: SkinType.PATTERN, id: 'ocean-starfish', name: 'Starfish', pattern: PatternType.GRADIENT, primaryColor: '#ff6347', secondaryColor: '#ffa07a', collection: SkinCollection.OCEAN },
  { type: SkinType.PATTERN, id: 'ocean-crab', name: 'Crab', pattern: PatternType.GRADIENT, primaryColor: '#dc143c', secondaryColor: '#ff6347', collection: SkinCollection.OCEAN },
  { type: SkinType.PATTERN, id: 'ocean-turtle', name: 'Turtle', pattern: PatternType.SCALES, primaryColor: '#228b22', secondaryColor: '#8b4513', collection: SkinCollection.OCEAN },
  { type: SkinType.PATTERN, id: 'ocean-pufferfish', name: 'Pufferfish', pattern: PatternType.SPOTTED, primaryColor: '#ffd700', secondaryColor: '#ffffff', collection: SkinCollection.OCEAN },
  { type: SkinType.PATTERN, id: 'ocean-angelfish', name: 'Angelfish', pattern: PatternType.STRIPED, primaryColor: '#ffff00', secondaryColor: '#000080', collection: SkinCollection.OCEAN },
  { type: SkinType.PATTERN, id: 'ocean-orca', name: 'Orca', pattern: PatternType.GRADIENT, primaryColor: '#000000', secondaryColor: '#ffffff', collection: SkinCollection.OCEAN },
  { type: SkinType.PATTERN, id: 'ocean-narwhal', name: 'Narwhal', pattern: PatternType.GRADIENT, primaryColor: '#b0c4de', secondaryColor: '#e6e6fa', collection: SkinCollection.OCEAN },

  // ═══════════════════════════════════════════════════════════════════════════════
  //  🦖 DINOSAURS - Roar! Every kid's favorite!
  // ═══════════════════════════════════════════════════════════════════════════════

  { type: SkinType.PATTERN, id: 'dino-trex', name: 'T-Rex', pattern: PatternType.SCALES, primaryColor: '#228b22', secondaryColor: '#006400', collection: SkinCollection.DINOSAURS },
  { type: SkinType.PATTERN, id: 'dino-triceratops', name: 'Triceratops', pattern: PatternType.GRADIENT, primaryColor: '#8b4513', secondaryColor: '#deb887', collection: SkinCollection.DINOSAURS },
  { type: SkinType.PATTERN, id: 'dino-stego', name: 'Stegosaurus', pattern: PatternType.ZIGZAG, primaryColor: '#228b22', secondaryColor: '#ff6347', collection: SkinCollection.DINOSAURS },
  { type: SkinType.PATTERN, id: 'dino-raptor', name: 'Velociraptor', pattern: PatternType.STRIPED, primaryColor: '#556b2f', secondaryColor: '#9acd32', collection: SkinCollection.DINOSAURS },
  { type: SkinType.PATTERN, id: 'dino-bronto', name: 'Brachiosaurus', pattern: PatternType.GRADIENT, primaryColor: '#6b8e23', secondaryColor: '#9acd32', collection: SkinCollection.DINOSAURS },
  { type: SkinType.PATTERN, id: 'dino-ptero', name: 'Pterodactyl', pattern: PatternType.GRADIENT, primaryColor: '#d2691e', secondaryColor: '#deb887', collection: SkinCollection.DINOSAURS },
  { type: SkinType.PATTERN, id: 'dino-ankylo', name: 'Ankylosaurus', pattern: PatternType.CHECKER, primaryColor: '#696969', secondaryColor: '#a0522d', collection: SkinCollection.DINOSAURS },
  { type: SkinType.PATTERN, id: 'dino-spino', name: 'Spinosaurus', pattern: PatternType.SCALES, primaryColor: '#4682b4', secondaryColor: '#2e8b57', collection: SkinCollection.DINOSAURS },
  { type: SkinType.PATTERN, id: 'dino-egg', name: 'Dino Egg', pattern: PatternType.SPOTTED, primaryColor: '#f5f5dc', secondaryColor: '#8b7355', collection: SkinCollection.DINOSAURS },
  { type: SkinType.PATTERN, id: 'dino-raptor-blue', name: 'Blue Raptor', pattern: PatternType.STRIPED, primaryColor: '#4682b4', secondaryColor: '#191970', collection: SkinCollection.DINOSAURS },
  { type: SkinType.PATTERN, id: 'dino-fossil', name: 'Fossil', pattern: PatternType.MARBLE, primaryColor: '#d2b48c', secondaryColor: '#f5f5dc', collection: SkinCollection.DINOSAURS },
  { type: SkinType.PATTERN, id: 'dino-rex-purple', name: 'Purple Rex', pattern: PatternType.SCALES, primaryColor: '#800080', secondaryColor: '#9932cc', collection: SkinCollection.DINOSAURS },

  // ═══════════════════════════════════════════════════════════════════════════════
  //  🍭 CANDY & SWEETS - Yummy looking skins!
  // ═══════════════════════════════════════════════════════════════════════════════

  { type: SkinType.PATTERN, id: 'candy-candycane', name: 'Candy Cane', pattern: PatternType.STRIPED, primaryColor: '#ff0000', secondaryColor: '#ffffff', collection: SkinCollection.CANDY },
  { type: SkinType.PATTERN, id: 'candy-lollipop', name: 'Lollipop', pattern: PatternType.RAINBOW, primaryColor: '#ff69b4', secondaryColor: '#00ffff', collection: SkinCollection.CANDY },
  { type: SkinType.PATTERN, id: 'candy-gummybear', name: 'Gummy Bear', pattern: PatternType.GRADIENT, primaryColor: '#ff4500', secondaryColor: '#ff6347', collection: SkinCollection.CANDY },
  { type: SkinType.PATTERN, id: 'candy-bubblegum', name: 'Bubblegum', pattern: PatternType.GRADIENT, primaryColor: '#ff69b4', secondaryColor: '#ffb6c1', collection: SkinCollection.CANDY },
  { type: SkinType.PATTERN, id: 'candy-cottoncandy', name: 'Cotton Candy', pattern: PatternType.GRADIENT, primaryColor: '#ffb6c1', secondaryColor: '#87ceeb', collection: SkinCollection.CANDY },
  { type: SkinType.PATTERN, id: 'candy-chocolate', name: 'Chocolate', pattern: PatternType.GRADIENT, primaryColor: '#8b4513', secondaryColor: '#3d1f0d', collection: SkinCollection.CANDY },
  { type: SkinType.PATTERN, id: 'candy-cupcake', name: 'Cupcake', pattern: PatternType.GRADIENT, primaryColor: '#ff69b4', secondaryColor: '#deb887', collection: SkinCollection.CANDY },
  { type: SkinType.PATTERN, id: 'candy-icecream', name: 'Ice Cream', pattern: PatternType.STRIPED, primaryColor: '#ffb6c1', secondaryColor: '#8b4513', collection: SkinCollection.CANDY },
  { type: SkinType.PATTERN, id: 'candy-sprinkles', name: 'Sprinkles', pattern: PatternType.SPOTTED, primaryColor: '#ff69b4', secondaryColor: '#00ff00', collection: SkinCollection.CANDY },
  { type: SkinType.PATTERN, id: 'candy-cookie', name: 'Cookie', pattern: PatternType.SPOTTED, primaryColor: '#deb887', secondaryColor: '#8b4513', collection: SkinCollection.CANDY },
  { type: SkinType.PATTERN, id: 'candy-donut', name: 'Donut', pattern: PatternType.SPOTTED, primaryColor: '#ffb6c1', secondaryColor: '#ff1493', collection: SkinCollection.CANDY },
  { type: SkinType.PATTERN, id: 'candy-marshmallow', name: 'Marshmallow', pattern: PatternType.GRADIENT, primaryColor: '#ffffff', secondaryColor: '#ffb6c1', collection: SkinCollection.CANDY },
  { type: SkinType.PATTERN, id: 'candy-jellybean', name: 'Jelly Bean', pattern: PatternType.RAINBOW, primaryColor: '#ff0000', secondaryColor: '#00ff00', collection: SkinCollection.CANDY },
  { type: SkinType.PATTERN, id: 'candy-mint', name: 'Mint Candy', pattern: PatternType.STRIPED, primaryColor: '#98ff98', secondaryColor: '#ffffff', collection: SkinCollection.CANDY },

  // ═══════════════════════════════════════════════════════════════════════════════
  //  🍓 FRUITS - Fresh and colorful!
  // ═══════════════════════════════════════════════════════════════════════════════

  { type: SkinType.PATTERN, id: 'fruit-strawberry', name: 'Strawberry', pattern: PatternType.SPOTTED, primaryColor: '#ff0000', secondaryColor: '#ffff00', collection: SkinCollection.FRUITS },
  { type: SkinType.PATTERN, id: 'fruit-watermelon', name: 'Watermelon', pattern: PatternType.STRIPED, primaryColor: '#00ff00', secondaryColor: '#ff6b6b', collection: SkinCollection.FRUITS },
  { type: SkinType.PATTERN, id: 'fruit-orange', name: 'Orange', pattern: PatternType.GRADIENT, primaryColor: '#ff8c00', secondaryColor: '#ffa500', collection: SkinCollection.FRUITS },
  { type: SkinType.PATTERN, id: 'fruit-grape', name: 'Grape', pattern: PatternType.GRADIENT, primaryColor: '#800080', secondaryColor: '#9932cc', collection: SkinCollection.FRUITS },
  { type: SkinType.PATTERN, id: 'fruit-banana', name: 'Banana', pattern: PatternType.GRADIENT, primaryColor: '#ffe135', secondaryColor: '#8b7355', collection: SkinCollection.FRUITS },
  { type: SkinType.PATTERN, id: 'fruit-apple', name: 'Apple', pattern: PatternType.GRADIENT, primaryColor: '#ff0000', secondaryColor: '#228b22', collection: SkinCollection.FRUITS },
  { type: SkinType.PATTERN, id: 'fruit-blueberry', name: 'Blueberry', pattern: PatternType.GRADIENT, primaryColor: '#4169e1', secondaryColor: '#800080', collection: SkinCollection.FRUITS },
  { type: SkinType.PATTERN, id: 'fruit-lemon', name: 'Lemon', pattern: PatternType.GRADIENT, primaryColor: '#fff44f', secondaryColor: '#9acd32', collection: SkinCollection.FRUITS },
  { type: SkinType.PATTERN, id: 'fruit-cherry', name: 'Cherry', pattern: PatternType.GRADIENT, primaryColor: '#dc143c', secondaryColor: '#8b0000', collection: SkinCollection.FRUITS },
  { type: SkinType.PATTERN, id: 'fruit-pineapple', name: 'Pineapple', pattern: PatternType.ZIGZAG, primaryColor: '#ffd700', secondaryColor: '#228b22', collection: SkinCollection.FRUITS },
  { type: SkinType.PATTERN, id: 'fruit-peach', name: 'Peach', pattern: PatternType.GRADIENT, primaryColor: '#ffcba4', secondaryColor: '#ff6347', collection: SkinCollection.FRUITS },
  { type: SkinType.PATTERN, id: 'fruit-kiwi', name: 'Kiwi', pattern: PatternType.SPOTTED, primaryColor: '#7cfc00', secondaryColor: '#2e8b57', collection: SkinCollection.FRUITS },
  { type: SkinType.PATTERN, id: 'fruit-mango', name: 'Mango', pattern: PatternType.GRADIENT, primaryColor: '#ff8c00', secondaryColor: '#ff4500', collection: SkinCollection.FRUITS },
  { type: SkinType.PATTERN, id: 'fruit-coconut', name: 'Coconut', pattern: PatternType.GRADIENT, primaryColor: '#8b4513', secondaryColor: '#ffffff', collection: SkinCollection.FRUITS },

  // ═══════════════════════════════════════════════════════════════════════════════
  //  🔥 FIRE & HOT - Blazing cool skins!
  // ═══════════════════════════════════════════════════════════════════════════════

  { type: SkinType.PATTERN, id: 'fire-flames', name: 'Fire', pattern: PatternType.FIRE, primaryColor: '#ff4500', secondaryColor: '#ffd700', collection: SkinCollection.FIRE },
  { type: SkinType.PATTERN, id: 'fire-lava', name: 'Lava', pattern: PatternType.LAVA, primaryColor: '#ff4500', secondaryColor: '#ffcc00', collection: SkinCollection.FIRE },
  { type: SkinType.PATTERN, id: 'fire-dragon', name: 'Dragon', pattern: PatternType.SCALES, primaryColor: '#dc143c', secondaryColor: '#ffd700', collection: SkinCollection.FIRE },
  { type: SkinType.PATTERN, id: 'fire-phoenix', name: 'Phoenix', pattern: PatternType.FIRE, primaryColor: '#ff8c00', secondaryColor: '#ffd700', collection: SkinCollection.FIRE },
  { type: SkinType.PATTERN, id: 'fire-volcano', name: 'Volcano', pattern: PatternType.EMBER, primaryColor: '#8b0000', secondaryColor: '#ff4500', collection: SkinCollection.FIRE },
  { type: SkinType.PATTERN, id: 'fire-campfire', name: 'Campfire', pattern: PatternType.EMBER, primaryColor: '#ff6600', secondaryColor: '#ffcc00', collection: SkinCollection.FIRE },
  { type: SkinType.PATTERN, id: 'fire-firework', name: 'Firework', pattern: PatternType.DISCO, primaryColor: '#ff0000', secondaryColor: '#ffd700', collection: SkinCollection.FIRE },
  { type: SkinType.PATTERN, id: 'fire-sun', name: 'Sun', pattern: PatternType.GRADIENT, primaryColor: '#ffd700', secondaryColor: '#ff8c00', collection: SkinCollection.FIRE },
  { type: SkinType.PATTERN, id: 'fire-hotpepper', name: 'Hot Pepper', pattern: PatternType.GRADIENT, primaryColor: '#ff0000', secondaryColor: '#228b22', collection: SkinCollection.FIRE },
  { type: SkinType.PATTERN, id: 'fire-ember', name: 'Ember', pattern: PatternType.EMBER, primaryColor: '#ff4500', secondaryColor: '#8b0000', collection: SkinCollection.FIRE },
  { type: SkinType.PATTERN, id: 'fire-torch', name: 'Torch', pattern: PatternType.FIRE, primaryColor: '#ffd700', secondaryColor: '#ff4500', collection: SkinCollection.FIRE },
  { type: SkinType.PATTERN, id: 'fire-magma', name: 'Magma', pattern: PatternType.LAVA, primaryColor: '#1a0000', secondaryColor: '#ff4500', collection: SkinCollection.FIRE },
  { type: SkinType.PATTERN, id: 'fire-inferno', name: 'Inferno', pattern: PatternType.FIRE, primaryColor: '#ff0000', secondaryColor: '#ffff00', collection: SkinCollection.FIRE },
  { type: SkinType.PATTERN, id: 'fire-blaze', name: 'Blaze', pattern: PatternType.FIRE, primaryColor: '#ff6600', secondaryColor: '#ff0000', collection: SkinCollection.FIRE },

  // ═══════════════════════════════════════════════════════════════════════════════
  //  ❄️ ICE & COLD - Frosty cool skins!
  // ═══════════════════════════════════════════════════════════════════════════════

  { type: SkinType.PATTERN, id: 'ice-snowflake', name: 'Snowflake', pattern: PatternType.CRYSTAL, primaryColor: '#ffffff', secondaryColor: '#87ceeb', collection: SkinCollection.ICE },
  { type: SkinType.PATTERN, id: 'ice-icicle', name: 'Icicle', pattern: PatternType.FROST, primaryColor: '#87ceeb', secondaryColor: '#ffffff', collection: SkinCollection.ICE },
  { type: SkinType.PATTERN, id: 'ice-frozen', name: 'Frozen', pattern: PatternType.FROST, primaryColor: '#00ffff', secondaryColor: '#e0ffff', collection: SkinCollection.ICE },
  { type: SkinType.PATTERN, id: 'ice-polarbear', name: 'Polar Bear', pattern: PatternType.GRADIENT, primaryColor: '#ffffff', secondaryColor: '#f0f0f0', collection: SkinCollection.ICE },
  { type: SkinType.PATTERN, id: 'ice-snowman', name: 'Snowman', pattern: PatternType.GRADIENT, primaryColor: '#ffffff', secondaryColor: '#ff6347', collection: SkinCollection.ICE },
  { type: SkinType.PATTERN, id: 'ice-glacier', name: 'Glacier', pattern: PatternType.CRYSTAL, primaryColor: '#00ced1', secondaryColor: '#e0ffff', collection: SkinCollection.ICE },
  { type: SkinType.PATTERN, id: 'ice-mintfresh', name: 'Mint Fresh', pattern: PatternType.GRADIENT, primaryColor: '#98ff98', secondaryColor: '#ffffff', collection: SkinCollection.ICE },
  { type: SkinType.PATTERN, id: 'ice-winter', name: 'Winter', pattern: PatternType.FROST, primaryColor: '#4169e1', secondaryColor: '#ffffff', collection: SkinCollection.ICE },
  { type: SkinType.PATTERN, id: 'ice-frost', name: 'Frost', pattern: PatternType.FROST, primaryColor: '#b0e0e6', secondaryColor: '#ffffff', collection: SkinCollection.ICE },
  { type: SkinType.PATTERN, id: 'ice-icecube', name: 'Ice Cube', pattern: PatternType.CRYSTAL, primaryColor: '#00ffff', secondaryColor: '#ffffff', collection: SkinCollection.ICE },
  { type: SkinType.PATTERN, id: 'ice-blizzard', name: 'Blizzard', pattern: PatternType.CRYSTAL, primaryColor: '#b9f2ff', secondaryColor: '#ffffff', collection: SkinCollection.ICE },
  { type: SkinType.PATTERN, id: 'ice-arctic', name: 'Arctic', pattern: PatternType.AURORA, primaryColor: '#00ff7f', secondaryColor: '#87ceeb', collection: SkinCollection.ICE },
  { type: SkinType.PATTERN, id: 'ice-popsicle', name: 'Popsicle', pattern: PatternType.STRIPED, primaryColor: '#ff6b6b', secondaryColor: '#4ecdc4', collection: SkinCollection.ICE },
  { type: SkinType.PATTERN, id: 'ice-frosty', name: 'Frosty', pattern: PatternType.FROST, primaryColor: '#e0ffff', secondaryColor: '#00bfff', collection: SkinCollection.ICE },

  // ═══════════════════════════════════════════════════════════════════════════════
  //  🚀 SPACE - To infinity and beyond!
  // ═══════════════════════════════════════════════════════════════════════════════

  { type: SkinType.PATTERN, id: 'space-galaxy', name: 'Galaxy', pattern: PatternType.GALAXY, primaryColor: '#191970', secondaryColor: '#ff00ff', collection: SkinCollection.SPACE },
  { type: SkinType.PATTERN, id: 'space-rocket', name: 'Rocket', pattern: PatternType.STRIPED, primaryColor: '#ff0000', secondaryColor: '#ffffff', collection: SkinCollection.SPACE },
  { type: SkinType.PATTERN, id: 'space-alien', name: 'Alien', pattern: PatternType.GRADIENT, primaryColor: '#32cd32', secondaryColor: '#000000', collection: SkinCollection.SPACE },
  { type: SkinType.PATTERN, id: 'space-moon', name: 'Moon', pattern: PatternType.SPOTTED, primaryColor: '#d3d3d3', secondaryColor: '#a9a9a9', collection: SkinCollection.SPACE },
  { type: SkinType.PATTERN, id: 'space-stars', name: 'Stars', pattern: PatternType.SPOTTED, primaryColor: '#191970', secondaryColor: '#ffffff', collection: SkinCollection.SPACE },
  { type: SkinType.PATTERN, id: 'space-planet', name: 'Planet', pattern: PatternType.STRIPED, primaryColor: '#daa520', secondaryColor: '#8b4513', collection: SkinCollection.SPACE },
  { type: SkinType.PATTERN, id: 'space-astronaut', name: 'Astronaut', pattern: PatternType.GRADIENT, primaryColor: '#ffffff', secondaryColor: '#4169e1', collection: SkinCollection.SPACE },
  { type: SkinType.PATTERN, id: 'space-comet', name: 'Comet', pattern: PatternType.FIRE, primaryColor: '#00bfff', secondaryColor: '#ffffff', collection: SkinCollection.SPACE },
  { type: SkinType.PATTERN, id: 'space-nebula', name: 'Nebula', pattern: PatternType.PLASMA, primaryColor: '#ff69b4', secondaryColor: '#9400d3', collection: SkinCollection.SPACE },
  { type: SkinType.PATTERN, id: 'space-blackhole', name: 'Black Hole', pattern: PatternType.VOID, primaryColor: '#000000', secondaryColor: '#9400d3', collection: SkinCollection.SPACE },
  { type: SkinType.PATTERN, id: 'space-saturn', name: 'Saturn', pattern: PatternType.STRIPED, primaryColor: '#f4a460', secondaryColor: '#deb887', collection: SkinCollection.SPACE },
  { type: SkinType.PATTERN, id: 'space-mars', name: 'Mars', pattern: PatternType.GRADIENT, primaryColor: '#cd5c5c', secondaryColor: '#8b0000', collection: SkinCollection.SPACE },
  { type: SkinType.PATTERN, id: 'space-ufo', name: 'UFO', pattern: PatternType.METALLIC, primaryColor: '#c0c0c0', secondaryColor: '#32cd32', collection: SkinCollection.SPACE },
  { type: SkinType.PATTERN, id: 'space-stardust', name: 'Stardust', pattern: PatternType.HOLOGRAPHIC, primaryColor: '#ffd700', secondaryColor: '#ffffff', collection: SkinCollection.SPACE },

  // ═══════════════════════════════════════════════════════════════════════════════
  //  🌈 RAINBOW & COLORS - Bright and happy!
  // ═══════════════════════════════════════════════════════════════════════════════

  { type: SkinType.PATTERN, id: 'rainbow-classic', name: 'Rainbow', pattern: PatternType.RAINBOW, primaryColor: '#ff0000', secondaryColor: '#ff00ff', collection: SkinCollection.RAINBOW },
  { type: SkinType.PATTERN, id: 'rainbow-sunset', name: 'Sunset', pattern: PatternType.SUNSET, primaryColor: '#ff6b35', secondaryColor: '#ff1493', collection: SkinCollection.RAINBOW },
  { type: SkinType.PATTERN, id: 'rainbow-sunrise', name: 'Sunrise', pattern: PatternType.GRADIENT, primaryColor: '#ffd700', secondaryColor: '#ff6b35', collection: SkinCollection.RAINBOW },
  { type: SkinType.PATTERN, id: 'rainbow-cottonsky', name: 'Cotton Candy Sky', pattern: PatternType.GRADIENT, primaryColor: '#ffb6c1', secondaryColor: '#87ceeb', collection: SkinCollection.RAINBOW },
  { type: SkinType.PATTERN, id: 'rainbow-neon', name: 'Neon', pattern: PatternType.NEON, primaryColor: '#ff00ff', secondaryColor: '#00ffff', collection: SkinCollection.RAINBOW },
  { type: SkinType.PATTERN, id: 'rainbow-pastel', name: 'Pastel', pattern: PatternType.GRADIENT, primaryColor: '#ffb6c1', secondaryColor: '#e6e6fa', collection: SkinCollection.RAINBOW },
  { type: SkinType.PATTERN, id: 'rainbow-tiedye', name: 'Tie Dye', pattern: PatternType.HYPNOTIC, primaryColor: '#ff00ff', secondaryColor: '#00ff00', collection: SkinCollection.RAINBOW },
  { type: SkinType.PATTERN, id: 'rainbow-glitter', name: 'Glitter', pattern: PatternType.HOLOGRAPHIC, primaryColor: '#ffd700', secondaryColor: '#ff69b4', collection: SkinCollection.RAINBOW },
  { type: SkinType.PATTERN, id: 'rainbow-disco', name: 'Disco', pattern: PatternType.DISCO, primaryColor: '#ff00ff', secondaryColor: '#00ffff', collection: SkinCollection.RAINBOW },
  { type: SkinType.PATTERN, id: 'rainbow-aurora', name: 'Aurora', pattern: PatternType.AURORA, primaryColor: '#00ff7f', secondaryColor: '#9400d3', collection: SkinCollection.RAINBOW },
  { type: SkinType.PATTERN, id: 'rainbow-prism', name: 'Prism', pattern: PatternType.HOLOGRAPHIC, primaryColor: '#ff69b4', secondaryColor: '#7fff00', collection: SkinCollection.RAINBOW },
  { type: SkinType.PATTERN, id: 'rainbow-spectrum', name: 'Spectrum', pattern: PatternType.CHROMATIC, primaryColor: '#ff0000', secondaryColor: '#0000ff', collection: SkinCollection.RAINBOW },

  // ═══════════════════════════════════════════════════════════════════════════════
  //  🦄 FANTASY & MAGIC - Magical creatures!
  // ═══════════════════════════════════════════════════════════════════════════════

  { type: SkinType.PATTERN, id: 'fantasy-unicorn', name: 'Unicorn', pattern: PatternType.RAINBOW, primaryColor: '#ffffff', secondaryColor: '#ff69b4', collection: SkinCollection.FANTASY },
  { type: SkinType.PATTERN, id: 'fantasy-fairy', name: 'Fairy', pattern: PatternType.HOLOGRAPHIC, primaryColor: '#ff69b4', secondaryColor: '#9400d3', collection: SkinCollection.FANTASY },
  { type: SkinType.PATTERN, id: 'fantasy-dragon', name: 'Dragon', pattern: PatternType.SCALES, primaryColor: '#9400d3', secondaryColor: '#ffd700', collection: SkinCollection.FANTASY },
  { type: SkinType.PATTERN, id: 'fantasy-wizard', name: 'Wizard', pattern: PatternType.GALAXY, primaryColor: '#4b0082', secondaryColor: '#ffd700', collection: SkinCollection.FANTASY },
  { type: SkinType.PATTERN, id: 'fantasy-princess', name: 'Princess', pattern: PatternType.GRADIENT, primaryColor: '#ff69b4', secondaryColor: '#ffd700', collection: SkinCollection.FANTASY },
  { type: SkinType.PATTERN, id: 'fantasy-knight', name: 'Knight', pattern: PatternType.METALLIC, primaryColor: '#c0c0c0', secondaryColor: '#4169e1', collection: SkinCollection.FANTASY },
  { type: SkinType.PATTERN, id: 'fantasy-mermaid', name: 'Mermaid', pattern: PatternType.SCALES, primaryColor: '#40e0d0', secondaryColor: '#9400d3', collection: SkinCollection.FANTASY },
  { type: SkinType.PATTERN, id: 'fantasy-ghost', name: 'Ghost', pattern: PatternType.GRADIENT, primaryColor: '#ffffff', secondaryColor: '#d3d3d3', collection: SkinCollection.FANTASY },
  { type: SkinType.PATTERN, id: 'fantasy-slime', name: 'Slime', pattern: PatternType.GRADIENT, primaryColor: '#32cd32', secondaryColor: '#90ee90', collection: SkinCollection.FANTASY },
  { type: SkinType.PATTERN, id: 'fantasy-crystal', name: 'Crystal', pattern: PatternType.CRYSTAL, primaryColor: '#e0ffff', secondaryColor: '#ff69b4', collection: SkinCollection.FANTASY },
  { type: SkinType.PATTERN, id: 'fantasy-vampire', name: 'Vampire', pattern: PatternType.GRADIENT, primaryColor: '#1a1a1a', secondaryColor: '#8b0000', collection: SkinCollection.FANTASY },
  { type: SkinType.PATTERN, id: 'fantasy-zombie', name: 'Zombie', pattern: PatternType.GRADIENT, primaryColor: '#98fb98', secondaryColor: '#556b2f', collection: SkinCollection.FANTASY },
  { type: SkinType.PATTERN, id: 'fantasy-phoenix', name: 'Phoenix', pattern: PatternType.FIRE, primaryColor: '#ffd700', secondaryColor: '#ff4500', collection: SkinCollection.FANTASY },
  { type: SkinType.PATTERN, id: 'fantasy-elf', name: 'Elf', pattern: PatternType.GRADIENT, primaryColor: '#228b22', secondaryColor: '#ffd700', collection: SkinCollection.FANTASY },

  // ═══════════════════════════════════════════════════════════════════════════════
  //  ⚡ SUPERHEROES - Pow! Zap! Boom!
  // ═══════════════════════════════════════════════════════════════════════════════

  { type: SkinType.PATTERN, id: 'hero-super-red', name: 'Super Red', pattern: PatternType.GRADIENT, primaryColor: '#ff0000', secondaryColor: '#ffd700', collection: SkinCollection.SUPERHEROES },
  { type: SkinType.PATTERN, id: 'hero-super-blue', name: 'Super Blue', pattern: PatternType.GRADIENT, primaryColor: '#0000ff', secondaryColor: '#ff0000', collection: SkinCollection.SUPERHEROES },
  { type: SkinType.PATTERN, id: 'hero-super-green', name: 'Super Green', pattern: PatternType.GRADIENT, primaryColor: '#00ff00', secondaryColor: '#800080', collection: SkinCollection.SUPERHEROES },
  { type: SkinType.PATTERN, id: 'hero-lightning', name: 'Lightning', pattern: PatternType.ELECTRIC, primaryColor: '#ffd700', secondaryColor: '#4169e1', collection: SkinCollection.SUPERHEROES },
  { type: SkinType.PATTERN, id: 'hero-shadow', name: 'Shadow', pattern: PatternType.VOID, primaryColor: '#1a1a1a', secondaryColor: '#4a4a4a', collection: SkinCollection.SUPERHEROES },
  { type: SkinType.PATTERN, id: 'hero-shield', name: 'Shield', pattern: PatternType.STRIPED, primaryColor: '#ff0000', secondaryColor: '#ffffff', collection: SkinCollection.SUPERHEROES },
  { type: SkinType.PATTERN, id: 'hero-web', name: 'Web', pattern: PatternType.STRIPED, primaryColor: '#ff0000', secondaryColor: '#0000ff', collection: SkinCollection.SUPERHEROES },
  { type: SkinType.PATTERN, id: 'hero-cape', name: 'Cape', pattern: PatternType.GRADIENT, primaryColor: '#ff0000', secondaryColor: '#000000', collection: SkinCollection.SUPERHEROES },
  { type: SkinType.PATTERN, id: 'hero-laser', name: 'Laser', pattern: PatternType.NEON, primaryColor: '#ff0000', secondaryColor: '#c0c0c0', collection: SkinCollection.SUPERHEROES },
  { type: SkinType.PATTERN, id: 'hero-thunder', name: 'Thunder', pattern: PatternType.LIGHTNING, primaryColor: '#9400d3', secondaryColor: '#ffd700', collection: SkinCollection.SUPERHEROES },
  { type: SkinType.PATTERN, id: 'hero-power', name: 'Power', pattern: PatternType.ELECTRIC, primaryColor: '#00ff00', secondaryColor: '#ffff00', collection: SkinCollection.SUPERHEROES },
  { type: SkinType.PATTERN, id: 'hero-stealth', name: 'Stealth', pattern: PatternType.CAMO, primaryColor: '#1a1a1a', secondaryColor: '#4a4a4a', collection: SkinCollection.SUPERHEROES },

  // ═══════════════════════════════════════════════════════════════════════════════
  //  ⚽ SPORTS - Game on!
  // ═══════════════════════════════════════════════════════════════════════════════

  { type: SkinType.PATTERN, id: 'sport-soccer', name: 'Soccer', pattern: PatternType.CHECKER, primaryColor: '#ffffff', secondaryColor: '#000000', collection: SkinCollection.SPORTS },
  { type: SkinType.PATTERN, id: 'sport-basketball', name: 'Basketball', pattern: PatternType.STRIPED, primaryColor: '#ff8c00', secondaryColor: '#000000', collection: SkinCollection.SPORTS },
  { type: SkinType.PATTERN, id: 'sport-baseball', name: 'Baseball', pattern: PatternType.STRIPED, primaryColor: '#ffffff', secondaryColor: '#ff0000', collection: SkinCollection.SPORTS },
  { type: SkinType.PATTERN, id: 'sport-football', name: 'Football', pattern: PatternType.STRIPED, primaryColor: '#8b4513', secondaryColor: '#ffffff', collection: SkinCollection.SPORTS },
  { type: SkinType.PATTERN, id: 'sport-tennis', name: 'Tennis', pattern: PatternType.GRADIENT, primaryColor: '#adff2f', secondaryColor: '#ffffff', collection: SkinCollection.SPORTS },
  { type: SkinType.PATTERN, id: 'sport-racing', name: 'Racing', pattern: PatternType.CHECKER, primaryColor: '#ff0000', secondaryColor: '#ffffff', collection: SkinCollection.SPORTS },
  { type: SkinType.PATTERN, id: 'sport-swimming', name: 'Swimming', pattern: PatternType.OCEAN, primaryColor: '#00bfff', secondaryColor: '#ffffff', collection: SkinCollection.SPORTS },
  { type: SkinType.PATTERN, id: 'sport-golf', name: 'Golf', pattern: PatternType.GRADIENT, primaryColor: '#228b22', secondaryColor: '#ffffff', collection: SkinCollection.SPORTS },
  { type: SkinType.PATTERN, id: 'sport-hockey', name: 'Hockey', pattern: PatternType.STRIPED, primaryColor: '#000000', secondaryColor: '#ffd700', collection: SkinCollection.SPORTS },
  { type: SkinType.PATTERN, id: 'sport-skateboard', name: 'Skateboard', pattern: PatternType.GLITCH, primaryColor: '#ff00ff', secondaryColor: '#00ffff', collection: SkinCollection.SPORTS },

  // ═══════════════════════════════════════════════════════════════════════════════
  //  🎄 HOLIDAYS - Festive fun!
  // ═══════════════════════════════════════════════════════════════════════════════

  { type: SkinType.PATTERN, id: 'holiday-christmas', name: 'Christmas', pattern: PatternType.STRIPED, primaryColor: '#ff0000', secondaryColor: '#00ff00', collection: SkinCollection.HOLIDAYS },
  { type: SkinType.PATTERN, id: 'holiday-halloween', name: 'Halloween', pattern: PatternType.STRIPED, primaryColor: '#ff8c00', secondaryColor: '#000000', collection: SkinCollection.HOLIDAYS },
  { type: SkinType.PATTERN, id: 'holiday-easter', name: 'Easter', pattern: PatternType.SPOTTED, primaryColor: '#ffb6c1', secondaryColor: '#98fb98', collection: SkinCollection.HOLIDAYS },
  { type: SkinType.PATTERN, id: 'holiday-valentine', name: 'Valentine', pattern: PatternType.GRADIENT, primaryColor: '#ff1493', secondaryColor: '#ff69b4', collection: SkinCollection.HOLIDAYS },
  { type: SkinType.PATTERN, id: 'holiday-stpatrick', name: 'St Patrick', pattern: PatternType.GRADIENT, primaryColor: '#228b22', secondaryColor: '#ffd700', collection: SkinCollection.HOLIDAYS },
  { type: SkinType.PATTERN, id: 'holiday-july4', name: '4th of July', pattern: PatternType.STRIPED, primaryColor: '#ff0000', secondaryColor: '#0000ff', collection: SkinCollection.HOLIDAYS },
  { type: SkinType.PATTERN, id: 'holiday-birthday', name: 'Birthday', pattern: PatternType.DISCO, primaryColor: '#ff69b4', secondaryColor: '#00ffff', collection: SkinCollection.HOLIDAYS },
  { type: SkinType.PATTERN, id: 'holiday-newyear', name: 'New Year', pattern: PatternType.HOLOGRAPHIC, primaryColor: '#ffd700', secondaryColor: '#c0c0c0', collection: SkinCollection.HOLIDAYS },
  { type: SkinType.PATTERN, id: 'holiday-thanksgiving', name: 'Thanksgiving', pattern: PatternType.GRADIENT, primaryColor: '#ff8c00', secondaryColor: '#8b4513', collection: SkinCollection.HOLIDAYS },
  { type: SkinType.PATTERN, id: 'holiday-hanukkah', name: 'Hanukkah', pattern: PatternType.STRIPED, primaryColor: '#0000ff', secondaryColor: '#ffffff', collection: SkinCollection.HOLIDAYS },
];

// ═══════════════════════════════════════════════════════════════════════════════
//  FLAG SKINS - Country flags
// ═══════════════════════════════════════════════════════════════════════════════

export const FLAG_SKINS: FlagSkin[] = [
  { type: SkinType.FLAG, id: 'flag-usa', name: 'USA', country: 'USA', colors: ['#bf0a30', '#ffffff', '#002868'] },
  { type: SkinType.FLAG, id: 'flag-uk', name: 'United Kingdom', country: 'United Kingdom', colors: ['#012169', '#ffffff', '#c8102e'] },
  { type: SkinType.FLAG, id: 'flag-germany', name: 'Germany', country: 'Germany', colors: ['#000000', '#dd0000', '#ffcc00'] },
  { type: SkinType.FLAG, id: 'flag-france', name: 'France', country: 'France', colors: ['#002395', '#ffffff', '#ed2939'] },
  { type: SkinType.FLAG, id: 'flag-spain', name: 'Spain', country: 'Spain', colors: ['#aa151b', '#f1bf00', '#aa151b'] },
  { type: SkinType.FLAG, id: 'flag-italy', name: 'Italy', country: 'Italy', colors: ['#009246', '#ffffff', '#ce2b37'] },
  { type: SkinType.FLAG, id: 'flag-brazil', name: 'Brazil', country: 'Brazil', colors: ['#009c3b', '#ffdf00', '#002776'] },
  { type: SkinType.FLAG, id: 'flag-japan', name: 'Japan', country: 'Japan', colors: ['#ffffff', '#bc002d', '#ffffff'] },
  { type: SkinType.FLAG, id: 'flag-south-korea', name: 'South Korea', country: 'South Korea', colors: ['#ffffff', '#c60c30', '#003478'] },
  { type: SkinType.FLAG, id: 'flag-china', name: 'China', country: 'China', colors: ['#de2910', '#ffde00', '#de2910'] },
  { type: SkinType.FLAG, id: 'flag-russia', name: 'Russia', country: 'Russia', colors: ['#ffffff', '#0039a6', '#d52b1e'] },
  { type: SkinType.FLAG, id: 'flag-canada', name: 'Canada', country: 'Canada', colors: ['#ff0000', '#ffffff', '#ff0000'] },
  { type: SkinType.FLAG, id: 'flag-australia', name: 'Australia', country: 'Australia', colors: ['#00008b', '#ffffff', '#ff0000'] },
  { type: SkinType.FLAG, id: 'flag-india', name: 'India', country: 'India', colors: ['#ff9933', '#ffffff', '#138808'] },
  { type: SkinType.FLAG, id: 'flag-mexico', name: 'Mexico', country: 'Mexico', colors: ['#006847', '#ffffff', '#ce1126'] },
  { type: SkinType.FLAG, id: 'flag-argentina', name: 'Argentina', country: 'Argentina', colors: ['#74acdf', '#ffffff', '#74acdf'] },
  { type: SkinType.FLAG, id: 'flag-netherlands', name: 'Netherlands', country: 'Netherlands', colors: ['#ae1c28', '#ffffff', '#21468b'] },
  { type: SkinType.FLAG, id: 'flag-sweden', name: 'Sweden', country: 'Sweden', colors: ['#006aa7', '#fecc00', '#006aa7'] },
  { type: SkinType.FLAG, id: 'flag-poland', name: 'Poland', country: 'Poland', colors: ['#ffffff', '#dc143c'] },
  { type: SkinType.FLAG, id: 'flag-turkey', name: 'Turkey', country: 'Turkey', colors: ['#e30a17', '#ffffff', '#e30a17'] },
  { type: SkinType.FLAG, id: 'flag-portugal', name: 'Portugal', country: 'Portugal', colors: ['#006600', '#ff0000'] },
  { type: SkinType.FLAG, id: 'flag-ukraine', name: 'Ukraine', country: 'Ukraine', colors: ['#005bbb', '#ffd500'] },
  { type: SkinType.FLAG, id: 'flag-belgium', name: 'Belgium', country: 'Belgium', colors: ['#000000', '#fdda24', '#ef3340'] },
  { type: SkinType.FLAG, id: 'flag-greece', name: 'Greece', country: 'Greece', colors: ['#0d5eaf', '#ffffff', '#0d5eaf'] },
  { type: SkinType.FLAG, id: 'flag-norway', name: 'Norway', country: 'Norway', colors: ['#ba0c2f', '#ffffff', '#00205b'] },
  { type: SkinType.FLAG, id: 'flag-finland', name: 'Finland', country: 'Finland', colors: ['#ffffff', '#003580', '#ffffff'] },
  { type: SkinType.FLAG, id: 'flag-ireland', name: 'Ireland', country: 'Ireland', colors: ['#169b62', '#ffffff', '#ff883e'] },
  { type: SkinType.FLAG, id: 'flag-austria', name: 'Austria', country: 'Austria', colors: ['#ed2939', '#ffffff', '#ed2939'] },
  { type: SkinType.FLAG, id: 'flag-denmark', name: 'Denmark', country: 'Denmark', colors: ['#c60c30', '#ffffff', '#c60c30'] },
  { type: SkinType.FLAG, id: 'flag-switzerland', name: 'Switzerland', country: 'Switzerland', colors: ['#ff0000', '#ffffff', '#ff0000'] },
  { type: SkinType.FLAG, id: 'flag-south-africa', name: 'South Africa', country: 'South Africa', colors: ['#007a4d', '#ffb612', '#de3831'] },
  { type: SkinType.FLAG, id: 'flag-egypt', name: 'Egypt', country: 'Egypt', colors: ['#ce1126', '#ffffff', '#000000'] },
  { type: SkinType.FLAG, id: 'flag-nigeria', name: 'Nigeria', country: 'Nigeria', colors: ['#008751', '#ffffff', '#008751'] },
  { type: SkinType.FLAG, id: 'flag-thailand', name: 'Thailand', country: 'Thailand', colors: ['#a51931', '#f4f5f8', '#2d2a4a'] },
  { type: SkinType.FLAG, id: 'flag-vietnam', name: 'Vietnam', country: 'Vietnam', colors: ['#da251d', '#ffff00', '#da251d'] },
  { type: SkinType.FLAG, id: 'flag-indonesia', name: 'Indonesia', country: 'Indonesia', colors: ['#ff0000', '#ffffff'] },
  { type: SkinType.FLAG, id: 'flag-philippines', name: 'Philippines', country: 'Philippines', colors: ['#0038a8', '#ce1126', '#fcd116'] },
  { type: SkinType.FLAG, id: 'flag-malaysia', name: 'Malaysia', country: 'Malaysia', colors: ['#cc0001', '#ffffff', '#010066'] },
  { type: SkinType.FLAG, id: 'flag-singapore', name: 'Singapore', country: 'Singapore', colors: ['#ed2939', '#ffffff'] },
  { type: SkinType.FLAG, id: 'flag-colombia', name: 'Colombia', country: 'Colombia', colors: ['#fcd116', '#003893', '#ce1126'] },
  { type: SkinType.FLAG, id: 'flag-chile', name: 'Chile', country: 'Chile', colors: ['#ffffff', '#0039a6', '#d52b1e'] },
  { type: SkinType.FLAG, id: 'flag-peru', name: 'Peru', country: 'Peru', colors: ['#d91023', '#ffffff', '#d91023'] },
  { type: SkinType.FLAG, id: 'flag-new-zealand', name: 'New Zealand', country: 'New Zealand', colors: ['#00247d', '#ffffff', '#cc142b'] },
  { type: SkinType.FLAG, id: 'flag-czech', name: 'Czech Republic', country: 'Czech Republic', colors: ['#ffffff', '#d7141a', '#11457e'] },
  { type: SkinType.FLAG, id: 'flag-romania', name: 'Romania', country: 'Romania', colors: ['#002b7f', '#fcd116', '#ce1126'] },
  { type: SkinType.FLAG, id: 'flag-hungary', name: 'Hungary', country: 'Hungary', colors: ['#cd2a3e', '#ffffff', '#436f4d'] },
  { type: SkinType.FLAG, id: 'flag-croatia', name: 'Croatia', country: 'Croatia', colors: ['#ff0000', '#ffffff', '#171796'] },
  { type: SkinType.FLAG, id: 'flag-jamaica', name: 'Jamaica', country: 'Jamaica', colors: ['#009b3a', '#fed100', '#000000'] },
  { type: SkinType.FLAG, id: 'flag-scotland', name: 'Scotland', country: 'Scotland', colors: ['#005eb8', '#ffffff', '#005eb8'] },
  { type: SkinType.FLAG, id: 'flag-wales', name: 'Wales', country: 'Wales', colors: ['#ffffff', '#00ab39', '#d4323e'] },
];

// ═══════════════════════════════════════════════════════════════════════════════
//  UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get all available skins
 */
export function getAllSkins(): PlayerSkin[] {
  return [...COLOR_SKINS, ...PATTERN_SKINS, ...FLAG_SKINS];
}

/**
 * Get skin by ID
 */
export function getSkinById(id: string): PlayerSkin | undefined {
  return getAllSkins().find((skin) => skin.id === id);
}

/**
 * Get skins by collection
 */
export function getSkinsByCollection(collection: SkinCollection): PatternSkin[] {
  return PATTERN_SKINS.filter((skin) => skin.collection === collection);
}

/**
 * Get default skin
 */
export function getDefaultSkin(): ColorSkin {
  return COLOR_SKINS[0]!;
}

/**
 * Convert hex color string to number
 */
export function hexToNumber(hex: string): number {
  return parseInt(hex.replace('#', ''), 16);
}

/**
 * Convert number to hex color string
 */
export function numberToHex(num: number): string {
  return '#' + num.toString(16).padStart(6, '0');
}

/**
 * Serialize skin for network transmission
 */
export function serializeSkin(skin: PlayerSkin): SkinData {
  const base: SkinData = {
    type: skin.type,
    id: skin.id,
    primaryColor: '',
  };

  switch (skin.type) {
    case SkinType.COLOR:
      base.primaryColor = skin.primaryColor;
      break;
    case SkinType.PATTERN:
      base.primaryColor = skin.primaryColor;
      if (skin.secondaryColor) {
        base.secondaryColor = skin.secondaryColor;
      }
      base.pattern = skin.pattern;
      break;
    case SkinType.FLAG:
      base.primaryColor = skin.colors[0] || '#ffffff';
      base.colors = skin.colors;
      break;
  }

  return base;
}

/**
 * Deserialize skin from network data
 */
export function deserializeSkin(data: SkinData): PlayerSkin | undefined {
  const existing = getSkinById(data.id);
  if (existing) {
    return existing;
  }

  switch (data.type) {
    case SkinType.COLOR:
      return {
        type: SkinType.COLOR,
        id: data.id,
        name: data.id,
        primaryColor: data.primaryColor,
      };
    case SkinType.PATTERN: {
      const patternSkin: PatternSkin = {
        type: SkinType.PATTERN,
        id: data.id,
        name: data.id,
        pattern: data.pattern || PatternType.SOLID,
        primaryColor: data.primaryColor,
      };
      if (data.secondaryColor) {
        patternSkin.secondaryColor = data.secondaryColor;
      }
      return patternSkin;
    }
    case SkinType.FLAG:
      return {
        type: SkinType.FLAG,
        id: data.id,
        name: data.id,
        country: data.id.replace('flag-', ''),
        colors: data.colors || [data.primaryColor],
      };
    default:
      return undefined;
  }
}

/**
 * Storage key for localStorage
 */
export const SKIN_STORAGE_KEY = 'wooorms_player_skin';

/**
 * Save skin to localStorage
 */
export function saveSkinToStorage(skin: PlayerSkin): void {
  try {
    const data = serializeSkin(skin);
    localStorage.setItem(SKIN_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Load skin from localStorage
 */
export function loadSkinFromStorage(): PlayerSkin {
  try {
    const stored = localStorage.getItem(SKIN_STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored) as SkinData;
      const skin = deserializeSkin(data);
      if (skin) {
        return skin;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return getDefaultSkin();
}
